import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { List as ImmutableList, List } from 'immutable';
import { debounce } from 'lodash-decorators';
import * as constants from '../../constants-types/constants';
import { FilterInfo, FilterType, User } from '../../core-data-model';
import { UserDataService } from '../../core-services';
import { TranslationService } from '../../translations';

/* WHY DOES THIS EXIST?!?!?!
So this was created expressively for the Share Groups interface. The validation for
adding a person is so complex that we only wanted one person added at a time. So originally
the interface was that when you add via pillbox it automatically does a server call.
This was deemed confusing so now we require that you hit the share button. This UI
concept of adding one person and then hitting a button doesn't really exist in our set
of components so this was created for that single purpose.
*/

@Component({
    selector: 'gated-pillbox',
    template: `
        <div
            [ngClass]="{ bottomSeparator: hasEntry() }"
            style="display: flex; max-height:54px; padding-bottom: 8px"
        >
            <ok-pillbox-with-typeahead
                style="margin-right: 10px; flex-grow: 1"
                [(ngModel)]="boundValue"
                (typeaheadOnTextChange)="typeaheadOnTextChange($event)"
                [typeaheadList]="typeaheadList"
                [typeaheadPlaceholder]="placeholder"
                [searchStyling]="true"
                [isLoadingPossibilities]="loadingUsers"
                [pillboxLimit]="1"
            >
                <ng-template #pillbox let-item="item">
                    {{ item.getPartialDisplayName() }}
                </ng-template>
                <ng-template #typeahead let-item="item">
                    {{ item.getFullDisplayName() }}
                </ng-template>
            </ok-pillbox-with-typeahead>
            <button
                class="button button-primary button-md"
                (click)="emitUser()"
                [disabled]="!enableButton()"
                [ngClass]="{
                    'button--loading': busy
                }"
            >
                {{ TR.key('text.common-terms.share') }}
            </button>
        </div>
    `,
    // Following is required for ControlValueAccessor
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: GatedPillboxComponent,
            multi: true
        }
    ],
    standalone: false
})
export class GatedPillboxComponent implements ControlValueAccessor {
    @Input()
    placeholder: string;

    @Input()
    checkForThirdPartyUsers = false;

    @Input()
    sharedUsers: List<User> = ImmutableList();

    @Input()
    busy = false;

    @Output()
    userAdded: EventEmitter<User> = new EventEmitter<User>();

    @Output()
    userRemoved: EventEmitter<User> = new EventEmitter<User>();

    typeaheadList: List<User> = ImmutableList();
    loadingUsers = false;

    individualSelection: List<User>;

    constructor(
        private user: UserDataService,
        public TR: TranslationService
    ) {}

    hasEntry() {
        return this.boundValue && this.boundValue.size > 0;
    }

    emitUser() {
        const firstItem = this.individualSelection.first();
        if (firstItem instanceof User) {
            this.userAdded.emit(firstItem);
        }
    }
    enableButton() {
        return this.boundValue && this.boundValue.size > 0;
    }

    typeaheadOnTextChange(textInBox: string) {
        if (!textInBox) {
            return;
        }
        this.loadingUsers = true;
        this.searchUsers(textInBox);
    }

    @debounce(constants.peopleSearchDelay)
    private searchUsers(searchText: string) {
        const filters: FilterInfo[] = [
            new FilterInfo({
                type: FilterType.isAccessEnabled
            }),
            new FilterInfo({
                type: FilterType.Search,
                parameters: { term: searchText }
            })
        ];
        const userSearchScope = this.checkForThirdPartyUsers ? 'all' : 'users';
        this.user
            .getUsers(5, filters, userSearchScope)
            .then((users) => {
                this.typeaheadList = ImmutableList(
                    users.filter(
                        (retrievedUser: User) =>
                            this.sharedUsers.findIndex(
                                (currentUser: User) =>
                                    currentUser.id === retrievedUser.id
                            ) === -1
                    )
                );
            })
            .finally(() => {
                this.loadingUsers = false;
            });
    }

    /** ***************************************/
    // Value needed by ControlValueAccessor - it is a no-op that is replaced by framework
    private propagateChange: Function = () => {
        /* No-op required by ControlValueAccessor interface - see registerOnChange */
    };

    get boundValue(): List<User> {
        return this.individualSelection;
    }
    set boundValue(val: List<User>) {
        this.individualSelection = val;
        this.propagateChange(this.individualSelection);
    }

    /* Meet ControlValueAccessor Interface */
    // Do NOT propagate changes up here
    writeValue(value: List<User>): void {
        this.individualSelection = value;
    }
    registerOnChange(fn: any): void {
        this.propagateChange = (changed: any) => fn.call(this, changed);
    }
    registerOnTouched(): void {
        /* No callback function registered OnTouched */
    }
    /** ***************************************/
}
